import { useEffect, useRef, useState, useLayoutEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export interface ContextMenuInputItem {
    id?: number;
    language: 'en' | 'es';
    text: string;
    text_es: string;
}

export interface MenuAction<T> {
    label: string | ReactNode;
    onClick: (data: T | null) => void;
    disabled?: boolean;
    shortcut?: string;
}

interface ContextMenuProps<T> {
    visible: boolean;
    x: number;
    y: number;
    actions: MenuAction<T>[];
    data: T | null;
    onClose: () => void;
    setContainerRef: (el: HTMLElement | null) => void;
}

export function ContextMenu<T>({
    visible,
    x,
    y,
    actions,
    data,
    onClose,
    setContainerRef,
}: ContextMenuProps<T>) {
    const menuRef = useRef<HTMLDivElement | null>(null);
    const [pos, setPos] = useState({ left: x, top: y });

    // Clamp to viewport so the menu doesn't overflow off-screen
    useLayoutEffect(() => {
        if (!visible) return;
        const el = menuRef.current;
        if (!el) return;

        const padding = 8;
        const rect = el.getBoundingClientRect();
        let left = x;
        let top = y;

        const vw = window.innerWidth;
        const vh = window.innerHeight;

        if (left + rect.width + padding > vw) {
            left = Math.max(padding, vw - rect.width - padding);
        }
        if (top + rect.height + padding > vh) {
            top = Math.max(padding, vh - rect.height - padding);
        }

        setPos({ left, top });
    }, [visible, x, y]);

    // Simple keyboard navigation (Up/Down + Enter)
    useEffect(() => {
        if (!visible) return;

        let idx = 0;
        const focusItem = (i: number) => {
            const items = menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]');
            if (!items || items.length === 0) return;
            idx = (i + items.length) % items.length;
            items[idx].focus();
        };

        const onKey = (e: KeyboardEvent) => {
            if (!visible) return;
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                focusItem(idx + 1);
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                focusItem(idx - 1);
            } else if (e.key === 'Enter') {
                e.preventDefault();
                const items =
                    menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]');
                const btn = items?.[idx];
                btn?.click();
            }
        };

        window.addEventListener('keydown', onKey);
        // focus first item shortly after open
        setTimeout(() => focusItem(0), 0);

        return () => window.removeEventListener('keydown', onKey);
    }, [visible]);

    if (!visible) return null;

    return createPortal(
        <div
            ref={(el) => {
                menuRef.current = el;
                setContainerRef(el);
            }}
            role="menu"
            aria-label="Context menu"
            style={{
                position: 'fixed',
                left: pos.left,
                top: pos.top,
                zIndex: 9999,
                minWidth: 200,
                background: '#fff',
                border: '1px solid rgba(0,0,0,0.1)',
                borderRadius: 8,
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                padding: 6,
            }}
        >
            {actions.map((a, i) => (
                <button
                    key={i}
                    role="menuitem"
                    disabled={a.disabled}
                    onClick={() => {
                        if (!a.disabled) {
                            a.onClick(data);
                            onClose();
                        }
                    }}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        padding: '8px 10px',
                        border: 'none',
                        background: 'transparent',
                        borderRadius: 6,
                        cursor: a.disabled ? 'not-allowed' : 'pointer',
                        color: a.disabled ? '#999' : '#053E68',
                    }}
                    onMouseDown={(e) => e.preventDefault()} // keep focus styles snappy
                    onKeyDown={(e) => e.stopPropagation()}
                    onMouseEnter={(e) => {
                        // optional hover focus effect
                        (e.currentTarget as HTMLButtonElement).focus();
                    }}
                >
                    <span style={{ pointerEvents: 'none', flexGrow: 1 }}>{a.label}</span>
                    {a.shortcut && <span style={{ opacity: 0.6, fontSize: 12 }}>{a.shortcut}</span>}
                </button>
            ))}
        </div>,
        document.body
    );
}
